import { useEffect } from 'react';

import { useAuth } from 'context/auth';
import type { ExtensionPort } from 'services/extensionAuth';
import {
  clearRuntimeError,
  connectToExtension,
  handOffTokenToExtension,
  isExtensionReachable,
} from 'services/extensionAuth';
import { probePortalExtension } from 'services/portalExtension';

import { EXTENSION_AUTH_MESSAGES } from './inboundReferrals/constants';

// Headless. Hands the browser extension a token when signed in and the extension is
// installed, and again whenever the extension requests one (its renewal path).
export default function ExtensionAuthManager() {
  const { token } = useAuth();

  useEffect(() => {
    // Nothing to do where the extension can't be reached (other browsers, not installed,
    // jsdom) — skip both the probe and the listener.
    if (!token || !isExtensionReachable()) return;

    probePortalExtension().then((installed) => {
      if (installed) handOffTokenToExtension().catch(() => {});
    });

    // Renewal requests arrive over a port we open to the extension — not a broadcast
    // window.postMessage. Only this SPA can open an externally_connectable port to the
    // pinned extension ID, so a request on the port is authenticated; arbitrary page
    // scripts can't drive mint cycles the way they could with a window message.
    let port: ExtensionPort | undefined;
    let reconnectTimer: ReturnType<typeof setTimeout> | undefined;
    let stopped = false;

    const open = () => {
      port = connectToExtension();
      if (!port) return;
      port.onMessage.addListener((msg) => {
        if (msg?.type === EXTENSION_AUTH_MESSAGES.handoffRequest) {
          handOffTokenToExtension().catch(() => {});
        }
      });
      // MV3 service workers idle out and drop the port; the extension holds the port in
      // worker memory, so a stale port means a renewal an hour later finds nothing. Reopen
      // on disconnect so a fresh worker always has a live channel. The delay keeps this from
      // spinning if the extension is gone (connect resolves, then disconnects immediately).
      port.onDisconnect.addListener(() => {
        clearRuntimeError();
        if (stopped) return;
        reconnectTimer = setTimeout(open, 1000);
      });
    };

    open();

    return () => {
      stopped = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      port?.disconnect();
    };
  }, [token]);

  return null;
}
